import type { HttpErrorResponse } from '@angular/common/http';

import type { CorrelationId } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@core/ngrx-legacy';
import type { SearchSegment } from '@core/search-types';

export const searchSegmentIdSelector = (segment: Pick<SearchSegment, 'id' | 'segmentType'>) =>
  segment.id || segment.segmentType;
export const { actions, reducer, selectors, namespace } =
  createEntityCollectionStore<SearchSegment>()('search-segments', {
    idSelector: searchSegmentIdSelector,
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, searchSegmentIdSelector);

      return {
        hide: (state, payload: { data: SearchSegment }) =>
          actions.removeOnePessimistic(state, { data: searchSegmentIdSelector(payload.data) }),
        hideSuccess: actions.removeOnePessimisticSuccess,
        hideFailed: actions.removeOnePessimisticFailed,

        save: (state, payload: { data: SearchSegment } & CorrelationId) =>
          adapter.markAsPending(state, {
            scope: null,
            entities: [payload.correlationId || searchSegmentIdSelector(payload.data)],
          }),
        saveSuccess: (state, payload: { data: SearchSegment } & CorrelationId) =>
          pipeState(
            state,
            next =>
              adapter.markAsLoaded(next, {
                scope: null,
                entities: [payload.correlationId || searchSegmentIdSelector(payload.data)],
              }),
            next => adapter.updateOne(next, payload.data),
          ),
        saveFailed: (
          state,
          payload: { error: HttpErrorResponse; data: SearchSegment } & CorrelationId,
        ) =>
          adapter.setError(state, payload.error, {
            scope: null,
            entities: [payload.correlationId || searchSegmentIdSelector(payload.data)],
          }),

        reorder: (state, payload: { data: SearchSegment[] }) => state,
        reorderSuccess: state => state,
        reorderFailed: (state, payload: { error: HttpErrorResponse }) => state,
      };
    },
  });

const SearchSegmentActions = <
  Pick<
    typeof actions,
    | 'fetchCollection'
    | 'fetchCollectionSuccess'
    | 'fetchCollectionFailed'
    | 'removeOnePessimistic'
    | 'removeOnePessimisticSuccess'
    | 'removeOnePessimisticFailed'
    | 'hide'
    | 'hideSuccess'
    | 'hideFailed'
    | 'save'
    | 'saveSuccess'
    | 'saveFailed'
    | 'reorder'
    | 'reorderSuccess'
    | 'reorderFailed'
  >
>actions;

export {
  SearchSegmentActions,
  reducer as SearchSegmentReducer,
  selectors as SearchSegmentSelectors,
  namespace as SearchSegmentNamespace,
};
